import React, {useContext, useEffect, useRef, useState} from 'react';
import { ExchangesFees, PaymentInfoContext } from '../../App';
import useStyles from './styles';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { Typography } from '@material-ui/core';
import { format } from 'date-fns';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { zeroDecimalCurrency, CurrencyFormats, selectedLanguage, PaymentModes } from '../../utils/index';
import { enUS, zhCN, ar, arSA, ru, es, fr, it, de, ja } from 'date-fns/locale';
import i18next from 'i18next';
import {isEqual} from "lodash";

const PaymentPlan = ({ formik, currencyList }) => {
  const [isScheduledFullPay, setScheduledFullPay] = useState(false);
  const paymentInfo = useContext(PaymentInfoContext);
  const exchangesFees = useContext(ExchangesFees);
  const locales = { enUS, zhCN, ar, arSA, ru, es, fr, it, de, ja };
  const [paymentPlan, setPaymentPlan] = useState(formik?.values?.isFullOrderAmountPaid ? 'full' : 'schedule');
  const [showSchedule, setShowSchedule] = useState();
  const classes = useStyles();
  const { t } = useTranslation();
  let lng = selectedLanguage(i18next.language);
  const locale = locales[lng];
  const dateFormat = 'yyyy MM dd';
  const paymentInfoRef = useRef();
  const CurrencyRef = useRef();

  useEffect(() => {
    if (paymentInfo) {
      const isPaymentInfoChanged = !isEqual(paymentInfoRef.current, paymentInfo);
      const isCurrencyChanged = !isEqual(CurrencyRef.current, formik.values.currency);
      const isPayLater = paymentInfo?.type === PaymentModes.PayLater

      const schedule = paymentInfo?.paymentSchedule?.map((item) => ({
        ...item,
        amountInOrderCurrency: getAmount(item.currencyToPay, item.amountInOrderCurrency),
        currencyToPay: formik.values.currency || item.currencyToPay
      }));
      formik.setFieldValue('paymentSchedule', schedule, paymentInfo?.type === PaymentModes.PayLater);
      if ((isPayLater && !isCurrencyChanged) || (isPayLater && isPaymentInfoChanged)) {
        setPaymentPlan('schedule');
        setShowSchedule(true);
      }
    }

    paymentInfoRef.current = paymentInfo;
    CurrencyRef.current = formik.values.currency;
  }, [paymentInfo, formik.values.currency]);

  useEffect(() => {
    futurePayment();
  }, []);

  // TODO: DRY. Move to utils
  const getAmount = (currency, amount) => {
    const currencyItem = currencyList?.find(x => x.iso === currency);
    if (currency === formik.values.currency) {
      return zeroDecimalCurrency(currencyItem) ? parseFloat(amount?.toFixed(0)).toFixed(2) : amount?.toFixed(2);
    }
    const exchangeItem = exchangesFees?.find((x) => x.currencyTo === formik.values.currency);
    let payAmount = amount * exchangeItem?.rate + ((amount * exchangeItem?.rate) / 100) * exchangeItem?.feePercAmount;
    return zeroDecimalCurrency(currencyItem) ? parseFloat(payAmount?.toFixed(0)).toFixed(2) : payAmount?.toFixed(2);
  };

  const handleChange = (e, value) => {
    e.preventDefault();
    if (!value) return;
    if (value) setPaymentPlan(value);
    formik.setFieldValue('isFullOrderAmountPaid', value === 'full');
  };
  const futurePayment = () => {
    const today = format(new Date(), dateFormat);
    if (paymentInfo?.paymentSchedule?.length && paymentInfo?.type === PaymentModes.PayNow) {
      const maxDate = format(
        new Date(
          Math.max(
            ...paymentInfo?.paymentSchedule.map((item) => {
              return new Date(item?.dateTime);
            })
          )
        ),
        dateFormat
      );
      const minDate = format(
        new Date(
          Math.min(
            ...paymentInfo?.paymentSchedule.map((item) => {
              return new Date(item?.dateTime);
            })
          )
        ),
        dateFormat
      );

      if (minDate.valueOf() === maxDate.valueOf() && minDate.valueOf() <= today.valueOf()) {
        formik.setFieldValue('isFullOrderAmountPaid', true);
        setScheduledFullPay(true);
      } else {
        formik.setFieldValue('isFullOrderAmountPaid', false);
        setScheduledFullPay(false);
      }
    } else if (paymentInfo?.paymentSchedule?.length && paymentInfo?.type === PaymentModes.PayLater) {
      formik.setFieldValue('isFullOrderAmountPaid', false);
      setScheduledFullPay(false);
    }
  };
  const toggleSchedule = (e) => {
    e.preventDefault();
    setShowSchedule(!showSchedule);
  };

  return formik?.values?.paymentSchedule?.length && isScheduledFullPay === false ? (
    <div className={classes.root}>
      <Typography variant="h3" className="input_label_text">
        {t('Payment_plan')}
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={paymentPlan}
        onChange={handleChange}
        orientation="vertical"
        className={classes.buttonGroup}>
        {
          paymentInfo?.showPayNow && (
            <ToggleButton value="full" aria-label="full" className={`${classes.button} pay_method_text`}>
              <div className="pay_method_text">
                {t('PAY_NOW')} - {formik.values.currency} {CurrencyFormats(formik.values.amount)}
              </div>
            </ToggleButton>
          )
        }
        <ToggleButton
          value="schedule"
          onClick={futurePayment}
          aria-label="schedule"
          className={`${classes.button} pay_method_text`}>
          <div className={classes.schedule}>
            <div className="pay_method_text">
              {t('Setup_payment')} {formik.values.currency} {CurrencyFormats(formik.values.amount)}
            </div>
            <div role="button" tabIndex={0} onKeyDown={toggleSchedule} onClick={toggleSchedule} className={`${classes.scheduleButton} payment_plan_link`}>
              <i className={clsx(classes.buttonIcon, showSchedule && classes.active)} />
              {t('Payment_plan_details')}
            </div>
          </div>
        </ToggleButton>
      </ToggleButtonGroup>
      {showSchedule && (
        <div className={classes.scheduleTable}>
          <div className={`${classes.scheduleTitle} payment_plan_title`}>{t('Payments_will_be_collected')}</div>
          <div className={classes.table}>
            {formik.values.paymentSchedule.map((item) => (
              <div key={item.id} className={classes.tableCell}>
                <div className="payment_plan_label">{format(new Date(item.dateTime), 'dd MMMM yyyy', { locale })}</div>
                <div className={`${classes.tableAmount} payment_plan_amount`}>
                  {zeroDecimalCurrency(currencyList?.find(x => x.iso === item.currencyToPay))
                    ? CurrencyFormats(parseFloat(item.amountInOrderCurrency).toFixed(0))
                    : CurrencyFormats(parseFloat(item.amountInOrderCurrency).toFixed(2))}{' '}
                  {item.currencyToPay}
                  {paymentInfo?.currency !== item.currencyToPay && <span>*</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  ) : null;
};

export default PaymentPlan;
